import React from 'react'
import { Trans } from '@lingui/macro'
import { useLocation } from 'react-router-dom'
import { css } from '@emotion/react'

import { SsoProviderEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import OneLoginImg from 'public/assets/img/oauth_onelogin.png'
import Spinner from '../../../components/common/spinner/Spinner'
import { useLoginPageQuery } from '../login/LoginPage.codegen'
import { useOauthLoginButtonsQuery } from './OauthLoginButtons.codegen'

import '../Auth.less'
import { NbSp } from 'front-end/src/utils/NbSp'
import { Flex, ButtonDark, VStack } from 'front-end/src/components'
import { brandColors } from 'shared/basicPalette'
import { OAUTH_NEXT_REDIRECT_STATE_PARAM_KEY } from 'shared/oauth'
import { getSSOProviders } from '../../../utils/SSOProviders'

export const prependOauthUrlWithHost = function (endpoint: string) {
  return `${location.origin}/${endpoint}`
}

export const buildOAuthUrl = (
  endpoint: SsoProviderEnum,
  organisationId: number,
  redirectTo: string | null
) => {
  let url: string = endpoint
  if (organisationId) {
    url = `auth/${endpoint}/organisation/${organisationId}`
  }
  if (redirectTo) {
    url = `${url}?${OAUTH_NEXT_REDIRECT_STATE_PARAM_KEY}=${redirectTo}`
  }

  return prependOauthUrlWithHost(url)
}

const getRedirectUrl = () => {
  const route = window.location.pathname
  if (route.startsWith('/login')) {
    return null
  }

  return route
}
type OauthLoginButtonsProps = {
  allowInstantLogin?: boolean
}

export const OauthLoginButtons: React.FC<OauthLoginButtonsProps> = ({
  allowInstantLogin
}) => {
  const { data, loading } = useOauthLoginButtonsQuery({
    fetchPolicy: 'cache-first'
  })

  const SSOProviders = React.useMemo(() => getSSOProviders(), [])
  const { data: loginPageData } = useLoginPageQuery({
    fetchPolicy: 'cache-first'
  })
  const location = useLocation()

  const handleOAuthBtnClick = (oauth) => {
    if (location.pathname !== '/dashboard') {
      sessionStorage.setItem('persistentUrl', location.pathname)
    }
    window.location.href = buildOAuthUrl(
      oauth.provider,
      organisation.id,
      getRedirectUrl()
    )
  }

  React.useEffect(() => {
    if (allowInstantLogin && data?.organisation?.oauthConfigs?.length === 1) {
      handleOAuthBtnClick(data.organisation.oauthConfigs[0])
    }
  }, [data, allowInstantLogin])

  if (loading || !data) {
    return <Spinner />
  }
  const { organisation } = data
  if (
    !organisation ||
    !organisation.oauthConfigs ||
    organisation.oauthConfigs.length === 0
  ) {
    return null
  }

  return (
    <div
      className="oauth"
      css={
        loginPageData?.organisation?.only_sso_login === false &&
        css`
          border-top: 1px solid #ecf0f1;
          padding-top: 24px;
        `
      }
    >
      {loginPageData?.organisation?.only_sso_login === false && (
        <span className="oauth__or">
          <Trans>or</Trans>
        </span>
      )}
      <VStack>
        {organisation.oauthConfigs
          .filter((oauthConfig) => oauthConfig.provider !== 'onelogin')
          .map((oauth, i) => {
            const staticConfig = SSOProviders[oauth.provider]
            if (!staticConfig) {
              console.warn('oauthConfig ignored: ', oauth)
              return
            }
            return (
              <ButtonDark
                key={i}
                backgroundColor={staticConfig.color}
                isFullWidth
                size="md"
                fontSize="md"
                justifyContent="flex-start"
                marginTop="8px"
                leftIcon={
                  staticConfig.icon && (
                    <Flex
                      height="24px"
                      width="36px"
                      mr="8px"
                      borderRight="1px solid rgba(0, 0, 0, 0.05)"
                    >
                      <img
                        height="100%"
                        width="auto"
                        src={staticConfig.icon}
                        alt={staticConfig.title}
                        title={staticConfig.title}
                      />
                    </Flex>
                  )
                }
                onClick={() => {
                  handleOAuthBtnClick(oauth)
                }}
              >
                <Trans>Sign in with</Trans>
                <NbSp />
                <strong>{staticConfig.title}</strong>
              </ButtonDark>
            )
          })}
        {organisation.oauthConfigs
          .filter((oauthConfig) => oauthConfig.provider === 'onelogin')
          .map((oauth) => {
            const title = 'OneLogin'
            return (
              <ButtonDark
                key={oauth.id}
                backgroundColor={brandColors.grey[800]}
                isFullWidth
                size="md"
                fontSize="md"
                justifyContent="flex-start"
                marginTop="8px"
                onClick={async () => {
                  const Oidc = await import('oidc-client')
                  const settings = {
                    redirect_uri: `${window.location.origin}/authSuccessCallback`,
                    response_type: 'id_token token',
                    scope: 'openid profile',

                    filterProtocolClaims: true,
                    loadUserInfo: true,
                    ...oauth.data
                  }
                  const mgr = new Oidc.UserManager(settings)
                  mgr.signinRedirect()
                }}
                leftIcon={
                  <Flex
                    height="24px"
                    width="36px"
                    mr="8px"
                    borderRight="1px solid rgba(200,200,200, 0.1)"
                  >
                    <img
                      alt={title}
                      title={title}
                      height="24px"
                      width="24px"
                      src={OneLoginImg}
                    />
                  </Flex>
                }
              >
                <Trans>Sign in with</Trans>
                <NbSp />

                <strong>{title}</strong>
              </ButtonDark>
            )
          })}
      </VStack>
    </div>
  )
}
